import { SortDirection } from "@episerver/ui-framework";
import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { Contact } from "../../ContactManager/model/contact";
import { fetchContacts, searchContacts } from "../../ContactManager/reducers/asyncThunks";
import { PaginationModel, SortModel } from "../model/organization";

export const initialAddContactsToOrganizationState = {
    organizationId: "",
    title: "Add contacts",
    open: false,
    keyword: "",
    contacts: [] as Contact[],
    selectedContacts: [] as Contact[],
    sort: {
        sortedColumn: "FullName",
        sortDirection: SortDirection.Ascending,
    } as SortModel,
    contactListPagination: {
        startRow: 0,
        rowsPerPage: 10,
        rowsPerPageOptions: [10, 15, 20],
        totalRowCount: 0,
    } as PaginationModel,
};

export const addContactsToOrganizationSlice = createSlice({
    name: "addContactsToOrganization",
    initialState: initialAddContactsToOrganizationState,
    reducers: {
        onChangeDialog: (state, action): void => {
            state.open = action.payload.open;
            state.contactListPagination.startRow = initialAddContactsToOrganizationState.contactListPagination.startRow;
            state.selectedContacts = [];
        },
        selectContact: (state, action) => {
            state.selectedContacts.push(action.payload);
        },
        deselectContact: (state, action) => {
            state.selectedContacts = state.selectedContacts.filter(
                (contact: Contact) => contact.primaryKeyId !== action.payload.primaryKeyId
            );
        },
        selectAllContacts: (state) => {
            state.selectedContacts = state.contacts;
        },
        deselectAllContacts: (state) => {
            state.selectedContacts = [];
        },
        sort: (state, action): void => {
            const { column, direction } = action.payload;
            state.sort = { sortDirection: direction, sortedColumn: column };
        },

        changeKeyword: (state, action): void => {
            state.keyword = action.payload;
        },
    },
    extraReducers: {
        [`${fetchContacts.fulfilled}`]: (state, action): void => {
            state.contacts = action.payload.contacts;
            state.contactListPagination.startRow = action.payload.pagingInfo.startRow;
            state.contactListPagination.rowsPerPage = action.payload.pagingInfo.rowsPerPage;
            state.contactListPagination.totalRowCount = action.payload.pagingInfo.totalRowCount;
            state.selectedContacts = [];
        },
        [`${searchContacts.fulfilled}`]: (state, action): void => {
            state.contacts = action.payload.contacts;
            state.contactListPagination.startRow = action.payload.pagingInfo.startRow;
            state.contactListPagination.rowsPerPage = action.payload.pagingInfo.rowsPerPage;
            state.contactListPagination.totalRowCount = action.payload.pagingInfo.totalRowCount;
            state.selectedContacts = [];
        },
    },
});

// Actions
export const {
    onChangeDialog,
    selectContact,
    deselectContact,
    selectAllContacts,
    deselectAllContacts,
    sort,
    changeKeyword,
} = addContactsToOrganizationSlice.actions;

// Selectors
export const contactsSelector = (state: RootState) => state.addContactsToOrganization.contacts;

export const sortSelector = (state: RootState) => state.addContactsToOrganization.sort;

export const contactListPaginationSelector = (state: RootState): PaginationModel =>
    state.addContactsToOrganization.contactListPagination;

export const selectedContactsSelector = (state: RootState): Contact[] =>
    state.addContactsToOrganization.selectedContacts;

export const isOpenDialogSelector = (state: RootState): boolean => state.addContactsToOrganization.open;

export const addContactsTitleSelector = (state: RootState): string => state.addContactsToOrganization.title;

export const oganizationIdSelector = (state: RootState): string => state.addContactsToOrganization.organizationId;

export const keywordSelector = (state: RootState): string => state.addContactsToOrganization.keyword;
